import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  CalendarDays,
  Banknote,
  Tags,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getRecentExpenses } from "../services/financeService";
import { createExpenseWithStats, deleteExpenseWithStats, updateExpenseWithStats } from "../services/financeWriteService";
import { clearCachedValue } from "../utils/cache";
import { usePanelTheme } from "../context/themeContext";
import { notify } from "../utils/notify";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";

export default function Expenses({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Cash");
  const [category, setCategory] = useState("Material");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileSection, setMobileSection] = useState("form");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");

  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const isGerman = lang === "de" || t.language === "Sprache" || t.language === "de";

  const expenseMethodLabels = {
    Cash: t.cash || "Cash",
    Bank: t.bank || (isGerman ? "Bank" : "Bankë"),
    Card: t.card || (isGerman ? "Karte" : "Kartelë"),
  };

  const expenseCategoryLabels = {
    Material: t.expenseCategoryMaterial || (isGerman ? "Material" : "Material"),
    Transport: t.expenseCategoryTransport || (isGerman ? "Transport" : "Transport"),
    Salary: t.expenseCategorySalary || (isGerman ? "Lohn" : "Pagë"),
    Tools: t.expenseCategoryTools || (isGerman ? "Werkzeuge" : "Vegla"),
    Other: t.expenseCategoryOther || (isGerman ? "Sonstige" : "Tjetër"),
  };

  const historyTitle =
    t.expenseHistoryTitle ||
    t.expenseHistory ||
    (isGerman ? "Ausgabenverlauf" : "Historia e shpenzimeve");

  const recordsLabel =
    t.records || (isGerman ? "Einträge" : "regjistrime");

  const prevLabel = t.prev || (isGerman ? "Zurück" : "Mbrapa");
  const nextLabel = t.next || (isGerman ? "Weiter" : "Para");
  const pageLabel = t.page || (isGerman ? "Seite" : "Faqja");
  const ofLabel = t.of || (isGerman ? "von" : "nga");


const loadProjects = async () => {
  try {
    const snap = await getDocs(query(collection(db, "projects"), orderBy("createdAt", "desc")));
    setProjects(
      snap.docs.map((item) => ({
        id: item.id,
        name: item.data()?.name || "",
      }))
    );
  } catch (error) {
    console.warn("Projects load skipped:", error);
    setProjects([]);
  }
};

const loadExpenses = async (force = false) => {
    try {
      setLoading(true);
      const data = await getRecentExpenses(force);
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading expenses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
    loadProjects();
  }, []);

  const totalExpenses = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [items]
  );

  const thisMonthExpenses = useMemo(() => {
    const now = new Date();
    return items.reduce((sum, item) => {
      const d = item?.date ? new Date(item.date) : null;
      if (d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
        return sum + (Number(item.amount) || 0);
      }
      return sum;
    }, 0);
  }, [items]);

  const cashExpenses = useMemo(
    () => items.filter((item) => item.method === "Cash").length,
    [items]
  );

  const topCategory = useMemo(() => {
    if (!items.length) return "-";
    const bucket = items.reduce((acc, item) => {
      const key = item.category || "Other";
      acc[key] = (acc[key] || 0) + (Number(item.amount) || 0);
      return acc;
    }, {});
    const sorted = Object.entries(bucket).sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || "-";
  }, [items]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetForm = () => {
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
    setMethod("Cash");
    setCategory("Material");
    setProjectId("");
    setProjectName("");
    setEditId(null);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setAmount(item.amount);
    setDescription(item.description || "");
    setDate(item.date || new Date().toISOString().slice(0, 10));
    setMethod(item.method || "Cash");
    setCategory(item.category || "Material");
    setProjectId(item.projectId || "");
    setProjectName(item.projectName || "");
    setMobileSection("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isGerman ? "Möchten Sie das wirklich löschen?" : "A jeni i sigurt që doni ta fshini këtë shpenzim?")) return;

    try {
      await deleteExpenseWithStats(id);
      notify.success(isGerman ? "Erfolgreich gelöscht" : "U fshi me sukses");
      clearCachedValue("bauheld_recent_expenses_v1");
      if (editId === id) resetForm();
      await loadExpenses(true);

      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      notify.error(isGerman ? "Fehler beim Löschen" : "Gabim gjatë fshirjes");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount) return;

    try {
      setSaving(true);
      const entry = {
        amount: Number(amount),
        description: description || "",
        date,
        method,
        category,
        projectId: projectId || "",
        projectName: projectName || projects.find((item) => item.id === projectId)?.name || "",
      };

      if (editId) {
        await updateExpenseWithStats(editId, entry);
        notify.success(isGerman ? "Erfolgreich aktualisiert" : "U përditësua me sukses");
      } else {
        await createExpenseWithStats(entry);
        notify.success(
          t.savedSuccessfully || (isGerman ? "Erfolgreich gespeichert" : "U ruajt me sukses")
        );
      }

      clearCachedValue("bauheld_recent_expenses_v1");
      resetForm();
      await loadExpenses(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error saving expense:", error);
      notify.error(
        t.saveErrorExpense || (isGerman ? "Fehler beim Speichern." : "Gabim gjatë ruajtjes.")
      );
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
      Number(value) || 0
    );

  const getMethodBadge = (value) => {
    const base = "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
    if (value === "Cash") return `${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400`;
    if (value === "Bank") return `${base} bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`;
    if (value === "Card") return `${base} bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400`;
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  };

  const getCategoryBadge = (value) => {
    const base = "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";
    if (value === "Material") return `${base} bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400`;
    if (value === "Transport") return `${base} bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-400`;
    if (value === "Salary") return `${base} bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400`;
    if (value === "Tools") return `${base} bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400`;
    return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
  };

  const inputClass = `h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
  }`;

  return (
    <PageShell>
      <SectionHeading
        title={t.expensesTitle || "Shpenzime"}
        subtitle={t.expensesSubtitle || "Regjistro shpenzimet dhe mbaj kostot nën kontroll."}
      />

      <div className="grid min-w-0 grid-cols-2 gap-2.5 xl:grid-cols-4">
        <StatCard
          title={t.totalExpenses || "Totali i shpenzimeve"}
          value={formatCurrency(totalExpenses)}
          subtitle={t.totalExpensesSubtitle || (isGerman ? "Alle Ausgaben" : "Të gjitha shpenzimet")}
          icon={Wallet}
          accent="amber"
        />
        <StatCard
          title={t.thisMonth || "Ky muaj"}
          value={formatCurrency(thisMonthExpenses)}
          subtitle={t.thisMonthSubtitle || (isGerman ? "Monatliche Performance" : "Performanca mujore")}
          icon={CalendarDays}
          accent="blue"
        />
        <StatCard
          title={t.cashExpensesCard || t.cashExpenses || "Pagesa Cash"}
          value={cashExpenses}
          subtitle={t.cashExpensesSubtitle || (isGerman ? "Bar-Ausgaben" : "Shpenzime cash")}
          icon={Banknote}
          accent="emerald"
        />
        <StatCard
          title={t.topCategory || t.topExpenseCategory || "Kategoria kryesore"}
          value={expenseCategoryLabels[topCategory] || topCategory}
          subtitle={t.topCategorySubtitle || t.topExpenseCategorySubtitle || (isGerman ? "Höchster Wert" : "Vlera më e lartë")}
          icon={Tags}
          accent="violet"
        />
      </div>

      <div className="xl:hidden">
        <PanelCard className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <UIButton
              type="button"
              onClick={() => setMobileSection("form")}
              theme={theme}
              variant={mobileSection === "form" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {editId ? (isGerman ? "Aktualisieren" : "Përditëso") : (t.addExpenseTitle || "Shto shpenzim")}
            </UIButton>

            <UIButton
              type="button"
              onClick={() => setMobileSection("history")}
              theme={theme}
              variant={mobileSection === "history" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {historyTitle}
            </UIButton>
          </div>
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[440px_minmax(0,1fr)]">
        <div className={mobileSection === "history" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3 className={`text-xl font-bold tracking-tight ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                  {editId ? (isGerman ? "Ausgabe bearbeiten" : "Përditëso shpenzimin") : (t.addExpenseTitle || "Shto shpenzim")}
                </h3>
              </div>

              {editId && (
                <UIButton
                  type="button"
                  onClick={resetForm}
                  theme={theme}
                  variant="softDanger"
                  size="sm"
                >
                  {t.cancel || "Anulo"}
                </UIButton>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                  {t.amount || "Shuma"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                  {t.description || "Përshkrimi"}
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={inputClass}
                />
              </div>


<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
  <div>
    <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
      {isGerman ? "Projekt" : "Projekti"}
    </label>
    <select
      value={projectId}
      onChange={(e) => {
        const nextId = e.target.value;
        const selectedProject = projects.find((item) => item.id === nextId);
        setProjectId(nextId);
        setProjectName(selectedProject?.name || "");
      }}
      className={inputClass}
    >
      <option value="">{isGerman ? "Kein verknüpftes Projekt" : "Pa projekt të lidhur"}</option>
      {projects.map((project) => (
        <option key={project.id} value={project.id}>
          {project.name || (isGerman ? "Ohne Namen" : "Pa emër")}
        </option>
      ))}
    </select>
  </div>
  <div>
    <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
      {isGerman ? "Projektname" : "Emri i projektit"}
    </label>
    <input
      type="text"
      value={projectName}
      onChange={(e) => setProjectName(e.target.value)}
      className={inputClass}
      placeholder={isGerman ? "Fallback Projektname" : "Emër projekti si fallback"}
    />
  </div>
</div>

<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
  <div>
    <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
      {t.date || "Data"}
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                    {isGerman ? "Zahlungsmethode" : t.method || "Metoda"}
                  </label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Cash">{expenseMethodLabels.Cash}</option>
                    <option value="Bank">{expenseMethodLabels.Bank}</option>
                    <option value="Card">{expenseMethodLabels.Card}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={`mb-2 block text-sm font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                  {t.category || "Kategoria"}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className={inputClass}
                >
                  <option value="Material">{expenseCategoryLabels.Material}</option>
                  <option value="Transport">{expenseCategoryLabels.Transport}</option>
                  <option value="Salary">{expenseCategoryLabels.Salary}</option>
                  <option value="Tools">{expenseCategoryLabels.Tools}</option>
                  <option value="Other">{expenseCategoryLabels.Other}</option>
                </select>
              </div>

              <UIButton
                type="submit"
                disabled={saving}
                theme={theme}
                variant="primary"
                size="xl"
                className="mt-2 w-full"
              >
                {saving
                  ? (t.savingExpense || "Duke ruajtur...")
                  : (editId ? (t.update || "Përditëso") : (t.saveExpense || "Ruaj shpenzimin"))}
              </UIButton>
            </form>
          </PanelCard>
        </div>

        <div className={mobileSection === "form" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-6 flex items-center justify-between gap-3">
              <div>
                <h3 className={`text-xl font-bold tracking-tight ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                  {historyTitle}
                </h3>
              </div>
              <div className={`rounded-2xl px-3 py-2 text-sm font-semibold ${currentTheme.surfaceSoft} ${currentTheme.muted}`}>
                {items.length} {recordsLabel}
              </div>
            </div>

            <div className="flex-1">
              {loading ? (
                <div className="py-10 text-center text-sm">{t.loading || "Duke ngarkuar..."}</div>
              ) : currentItems.length === 0 ? (
                <div className="py-10 text-center text-sm">{t.noData || "Nuk ka të dhëna."}</div>
              ) : (
                <div className="space-y-3">
                  {currentItems.map((item, index) => (
                    <div
                      key={item.id || index}
                      className={`flex flex-col gap-4 rounded-[20px] border p-4 transition shadow-[0_12px_30px_rgba(15,23,42,0.04)] md:flex-row md:items-center md:justify-between ${
                        theme === "dark"
                          ? "border-slate-700 bg-slate-900/60 hover:border-slate-600 hover:bg-slate-900"
                          : "border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className={`text-base font-bold ${theme === "dark" ? "text-slate-100" : "text-slate-900"}`}>
                            {formatCurrency(item.amount)}
                          </h4>
                          <span className={getMethodBadge(item.method)}>
                            {expenseMethodLabels[item.method] || item.method || expenseMethodLabels.Cash}
                          </span>
                          <span className={getCategoryBadge(item.category)}>
                            {expenseCategoryLabels[item.category] || item.category || expenseCategoryLabels.Other}
                          </span>
                        </div>
                        <p className={`mt-1.5 truncate text-sm font-medium ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                          {item.description || t.noDescription || "Pa përshkrim"}
                        </p>
                        <div className={`mt-1 text-xs font-semibold ${currentTheme.muted}`}>
                          {item.date || "-"}
                        </div>
                      </div>

                      <div className="mt-2 flex items-center gap-2 md:mt-0">
                        <UIButton
                          type="button"
                          onClick={() => handleEdit(item)}
                          theme={theme}
                          variant="primary"
                          size="sm"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{t.edit || "Ndrysho"}</span>
                        </UIButton>

                        <UIButton
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          theme={theme}
                          variant="softDanger"
                          size="sm"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{t.delete || "Fshij"}</span>
                        </UIButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
                <UIButton
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  theme={theme}
                  variant="secondary"
                  size="md"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">{prevLabel}</span>
                </UIButton>

                <span className={`text-sm font-semibold ${currentTheme.muted}`}>
                  {pageLabel} {currentPage} {ofLabel} {totalPages}
                </span>

                <UIButton
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  theme={theme}
                  variant="secondary"
                  size="md"
                >
                  <span className="hidden sm:inline">{nextLabel}</span>
                  <ChevronRight className="h-4 w-4" />
                </UIButton>
              </div>
            )}
          </PanelCard>
        </div>
      </div>
    </PageShell>
  );
}
